import React from 'react';
import { Users, Heart, GraduationCap, HandHeart, Calendar, BookOpen, Droplet, AlertCircle, Stethoscope, Check } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { useLanguage } from '../context/LanguageContext';
export function AboutPage() {
  const {
    t
  } = useLanguage();
  const purposeItems = [{
    icon: Users,
    title: 'Alumni Connection',
    description: 'Maintaining strong ties with Khyber Medical College and fostering lifelong relationships among graduates.'
  }, {
    icon: Heart,
    title: 'Community Engagement',
    description: 'Organizing events, seminars, and social gatherings that strengthen our community bonds.'
  }, {
    icon: GraduationCap,
    title: 'Medical Education',
    description: 'Supporting the next generation through scholarships, mentorship programs, and educational initiatives.'
  }, {
    icon: HandHeart,
    title: 'Humanitarian Relief',
    description: 'Providing medical aid, disaster relief, and healthcare support to underserved communities in Pakistan.'
  }];
  const milestones = [{
    icon: Calendar,
    title: 'Yearly Social Gatherings',
    description: 'Regular annual events bringing our community together for networking and cultural celebration.'
  }, {
    icon: BookOpen,
    title: 'Computers and Magazines for KMC Library',
    description: 'Supporting Khyber Medical College with essential resources for medical education.'
  }, {
    icon: Droplet,
    title: 'Flood Nowshehra 2010 Relief Funds',
    description: 'Emergency humanitarian response providing critical aid to flood-affected communities.'
  }, {
    icon: AlertCircle,
    title: 'Earthquake 2005 Hazara/Battagram',
    description: 'Major relief mission delivering medical aid and supplies following the devastating earthquake.'
  }, {
    icon: Stethoscope,
    title: 'Clinical Attachment for Young Doctors',
    description: 'Mentorship program providing valuable clinical experience and guidance for emerging medical professionals.'
  }];
  const journeyMilestones = [{
    title: 'Foundation',
    description: 'KMA was started by a group of doctors and the aim in the beginning was to arrange social and cultural events. Dr Khalid Saeed Haq was the first President of the Association.'
  }, {
    title: 'Charitable Status',
    description: 'KMA Constitution was adopted in year 2000 and approved in March 2003.'
  }, {
    title: 'Charity Registration',
    description: 'The registration with Charity Commission was completed on 29/04/2003 and Charity number is 1097262.'
  }];
  return <>
      <SEO title={t('nav.about')} description={t('about.hero.subtitle')} data-id="element-151" />

      {/* Hero */}
      <div className="bg-[#1e5f4f] text-white py-16 md:py-20" data-id="element-152">
        <div className="container mx-auto px-4 md:px-6" data-id="element-153">
          <FadeIn className="max-w-3xl mx-auto text-center" data-id="element-154">
            <img src="https://cdn.magicpatterns.com/uploads/aA4YJyXsgBXST459kH7g7H/New_Logo_bg_remove.png" alt="KMA UK Logo" className="h-28 w-28 mx-auto mb-6 object-contain" data-id="element-155" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-white" data-id="element-156">
              About KMA UK
            </h1>
            <p className="text-xl text-white leading-relaxed" data-id="element-157">
              A trusted community of medical professionals united by shared
              values, dedicated to excellence in healthcare and service to
              humanity.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Who We Are */}
      <Section data-id="element-158">
        <div className="max-w-4xl mx-auto" data-id="element-159">
          <FadeIn data-id="element-160">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4 text-center" data-id="element-161">
              Who We Are
            </h2>
            <div className="text-gray-600 leading-relaxed space-y-3 text-base md:text-lg text-left mb-8" data-id="element-162">
              <p data-id="element-163">
                The Khyber Medical Association UK (KMA-UK) is a professional and
                alumni organisation representing medical graduates and
                healthcare professionals of Khyber Pakhtunkhwa (KP) origin,
                primarily based in the United Kingdom and Ireland.
              </p>
              <p data-id="element-164">
                Although rooted in the legacy of Khyber Medical College, KMA-UK
                is an inclusive and diverse association. Our membership includes
                graduates from all medical colleges and institutions across
                Khyber Pakhtunkhwa, medical colleges throughout Pakistan and
                institutions worldwide. What unites our members is their shared
                origin from KP and a common commitment to excellence in
                healthcare, professionalism, ethical practice, and community
                service.
              </p>
              <p data-id="element-165">
                Founded by a group of visionary doctors who recognised the need
                for a unified and supportive professional platform, KMA-UK has
                evolved into a respected organisation known for its dedication
                to medical ethics, professional development, education, and
                humanitarian and charitable initiatives.
              </p>
              <p data-id="element-166">
                Our members span all stages of the medical profession, including
                consultants, general practitioners, junior doctors, medical
                students, and allied health professionals. While our primary
                focus is on professionals based in the UK and Ireland, we also
                welcome members from other European countries who participate in
                our academic programmes, annual gatherings, charitable
                activities and wider organisational initiatives.
              </p>
              <p data-id="element-167">
                Through collaboration, inclusivity, and shared values, KMA-UK
                seeks to strengthen professional networks, support career
                development, and contribute positively to healthcare and society
                at large.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-168">
            <div className="relative" data-id="element-169">
              <div className="absolute -inset-4 bg-primary-50 rounded-2xl transform rotate-3" data-id="element-170" />
              <img src="https://cdn.magicpatterns.com/uploads/rgrSJawmhW3zF59H7uaMNf/Khabar_medical_college.jpg" alt="Khyber Medical College campus" className="relative rounded-xl shadow-xl w-full" data-id="element-171" />
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Milestones */}
      <Section background="gray" data-id="element-172">
        <div className="max-w-5xl mx-auto" data-id="element-173">
          <FadeIn data-id="element-174">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-2 text-center" data-id="element-175">
              Milestones
            </h2>
            <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto" data-id="element-176">
              Key achievements and initiatives that define our commitment to
              medical excellence and humanitarian service.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-177">
            {milestones.map((milestone, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-178">
                <Card className="h-full border-primary-100 hover:shadow-lg transition-shadow" data-id="element-179">
                  <CardContent className="p-5" data-id="element-180">
                    <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-3" data-id="element-181">
                      <milestone.icon className="h-6 w-6" data-id="element-182" />
                    </div>
                    <h3 className="text-lg font-bold text-primary-900 mb-2" data-id="element-183">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-184">
                      {milestone.description}
                    </p>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Our Purpose */}
      <Section data-id="element-185">
        <div className="max-w-4xl mx-auto" data-id="element-186">
          <FadeIn data-id="element-187">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-3 text-left" data-id="element-188">
              Our Purpose
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8 text-left" data-id="element-189">
              We exist to strengthen the bonds among medical professionals while
              extending our expertise and resources to serve communities in
              need.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-190">
            {purposeItems.map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-191">
                <div className="text-center" data-id="element-192">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto" data-id="element-193">
                    <item.icon className="h-8 w-8" data-id="element-194" />
                  </div>
                  <h3 className="text-lg font-bold text-primary-900 mb-2" data-id="element-195">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-196">
                    {item.description}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Our Journey */}
      <Section background="gray" data-id="element-197">
        <div className="max-w-3xl mx-auto" data-id="element-198">
          <FadeIn data-id="element-199">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-8 text-left" data-id="element-200">
              Our Journey
            </h2>
          </FadeIn>

          <div className="space-y-6 relative before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-primary-200" data-id="element-201">
            {journeyMilestones.map((milestone, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-202">
                <div className="relative pl-8" data-id="element-203">
                  <div className="absolute left-0 top-1 w-3 h-3 rounded-full bg-primary-600 -translate-x-[5px]" data-id="element-204" />
                  <h3 className="text-xl font-bold text-primary-900 mb-2" data-id="element-205">
                    {milestone.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-206">
                    {milestone.description}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Our Future Vision */}
      <Section data-id="element-207">
        <div className="max-w-4xl mx-auto" data-id="element-208">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-209">
            <FadeIn data-id="element-210">
              <div className="relative" data-id="element-211">
                <img src="https://cdn.magicpatterns.com/uploads/mQkggC4qPaSKoC7GPCzSzK/our_future_vision.jpg" alt="KMA UK members in discussion" className="rounded-xl shadow-xl w-full" data-id="element-212" />
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-213">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4 text-left" data-id="element-214">
                Our Future Vision
              </h2>
              <div className="text-gray-600 leading-relaxed space-y-3 text-base md:text-lg text-left" data-id="element-215">
                <p data-id="element-216">
                  We envision a future where KMA UK serves as a model for
                  professional medical associations worldwide—one that
                  seamlessly blends career advancement with meaningful
                  humanitarian impact.
                </p>
                <p data-id="element-217">
                  Our goals include expanding our charitable reach to more
                  communities in Pakistan, establishing sustainable healthcare
                  programs, creating more opportunities for young doctors
                  through mentorship and scholarships, and strengthening the
                  global network of KP medical professionals.
                </p>
                <p data-id="element-218">
                  We are committed to leveraging technology to connect our
                  members across continents, facilitating knowledge sharing, and
                  ensuring that distance never diminishes our collective impact.
                </p>
                <p className="font-medium text-primary-800" data-id="element-219">
                  Together, we are building a legacy of medical excellence and
                  compassionate service that will benefit generations to come.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>
    </>;
}